import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import AutoSuggest from "../../../../components/Form/AutoSuggest";
import Button, { Color } from "../../../../components/Form/Button";
import SkeletonComponent from "../../../../components/Generic/Skeleton";
import SubHeader from "../../../../components/Generic/SubHeader";
import UITabMaterial, { PanelProps } from "../../../../components/Generic/UITabMaterial";
import { PRIVATE_ROUTES } from "../../../../constants";
import { useAppState } from "../../../../context/StateContext";
import { useQueryURL } from "../../../../hooks/useQueryURL";
import { Project } from "../../../../models/project";
import { UserProject } from "../../../../models/user.project";
import AccountsService from "../../../../services/account.service";
import AgentService from "../../../../services/agent.service";
import CustomerService from "../../../../services/customer.service";
import SupplierService from "../../../../services/supplier.service";
import { CompanyProfile } from "../../../../util/enums";
import Account from "./Account";
import { AgentListPage } from "../../../../modules/moving/external-agent/pages/AgentListPage";
import { CustomerScreen } from "./Customer";
import { useActionNetwork } from "./hooks/useAction";
import useStyles, { ProjectFieldContainer, ProjectLabel } from "./styles";
import Supplier from "./Supplier";
import { checkResourceCan } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { isMoving } from "Util/validation";

interface SearchParams {
  tab?: string;
}

export const NetworkPage = () => {
  const { t: translate } = useTranslation();
  const queries = useQueryURL<SearchParams>();
  const history = useHistory();
  const { useUserProjectViewUpdate } = useActionNetwork();
  const { mutate: updateUserProjectView, isLoading: loadingProjects } = useUserProjectViewUpdate();

  const [customersCount, setCustomersCount] = useState<number>(0);
  const [agentsCount, setAgentsCount] = useState<number>(0);
  const [accountsCount, setAccountsCount] = useState<number>(0);
  const [suppliersCount, setSuppliersCount] = useState<number>(0);
  const { currentUser, isFMVersion, userPermissions } = useAppState();
  const [updateProjectTimeOut, setUpdateProjectTimeOut] = useState<any>(0);
  const [isProjectsFocused, setIsProjectsFocused] = useState<boolean>(false);
  const [isAsyncUpdate, setIsAsyncUpdate] = useState<boolean>(false);
  const [hasProjectChanged, setHasProjectChanged] = useState<boolean>(false);
  const { tab } = queries("tab");

  const initialProjectToViewState = useMemo(
    () => currentUser?.projects.filter((project) => !!project.projectView) ?? [],
    [currentUser?.projects]
  );
  const [projects, setProjects] = useState<Project[]>(initialProjectToViewState);

  const classes = useStyles();
  const [networkPermission, setNetworkPermission] = useState<{
    // customers: {
    //   view: boolean;
    // };
    agents: {
      view: boolean;
    };
    accounts: {
      view: boolean;
    };
    suppliers: {
      view: boolean;
    };
  }>({
    // customers: {
    //   view: false,
    // },
    agents: {
      view: false,
    },
    accounts: {
      view: false,
    },
    suppliers: {
      view: false,
    },
  });

  const handleUpdateUserProjectsView = useCallback(() => {
    setHasProjectChanged(false);
    setIsAsyncUpdate(false);
    if (currentUser && currentUser?.id && initialProjectToViewState) {
      const userProjects: UserProject[] = [
        ...initialProjectToViewState,
        ...projects.filter(
          (project) => !initialProjectToViewState.map((project) => project.id).includes(project.id)
        ),
      ]?.map((project) => {
        return {
          companyId: currentUser.companyId,
          projectId: project.id,
          userId: currentUser?.id,
          projectView: !!projects.find((projectSelected) => projectSelected.id === project.id),
        };
      });

      updateUserProjectView({ userProjects });
    }
  }, [projects]);

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setNetworkPermission({
        agents: {
          view: checkResourceCan(userPermissions, ResourceName.AGENTS, ResourceCanEnum.VIEW),
        },
        accounts: {
          view: checkResourceCan(userPermissions, ResourceName.ACCOUNTS, ResourceCanEnum.VIEW),
        },
        suppliers: {
          view:
            currentUser?.company?.profile?.initials !== CompanyProfile.MV &&
            checkResourceCan(userPermissions, ResourceName.SUPPLIERS, ResourceCanEnum.VIEW),
        },
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (isAsyncUpdate) {
      handleUpdateUserProjectsView();
    }
  }, [isAsyncUpdate]);

  useQuery(
    ["customers.active.count"],
    () =>
      CustomerService.count({
        companyId: currentUser?.companyId,
        status: "Active",
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => {
        if (response && response.data) setCustomersCount(response.data);
      },
    }
  );

  useQuery(
    ["agents.active.count"],
    () =>
      AgentService.count({
        createdCompanyId: currentUser?.companyId,
        status: "Active",
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => {
        if (response && response.data) setAgentsCount(response.data);
      },
    }
  );

  useQuery(
    ["accounts.active.count"],
    () =>
      AccountsService.count({
        companyId: currentUser?.companyId,
        status: "Active",
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => {
        if (response && response.data) setAccountsCount(response.data);
      },
    }
  );

  useQuery(
    ["suppliers.active.count"],
    () =>
      SupplierService.count({
        companyId: currentUser?.companyId,
        active: 1,
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => {
        if (response && response.data) setSuppliersCount(response.data);
      },
    }
  );

  const customersPanel: PanelProps = {
    menuItem: translate("customers"),
    badgeValue: customersCount,
    className: classes.uiTab,
    key: "customers",
    render: () => <CustomerScreen />,
  };

  const agentsPanel: PanelProps = {
    menuItem: translate("agents"),
    badgeValue: agentsCount,
    className: classes.uiTab,
    key: "agents",
    render: () => <AgentListPage />,
  };

  const suppliersPanel: PanelProps = {
    menuItem: translate("suppliers"),
    badgeValue: suppliersCount,
    className: classes.uiTab,
    key: "suppliers",
    render: () => <Supplier />,
  };

  const accountsPanel: PanelProps = {
    menuItem: translate("accounts"),
    badgeValue: accountsCount,
    className: classes.uiTab,
    key: "accounts",
    render: () => <Account />,
  };

  const handleNetworkTabsByPermission = () => {
    let panels = [];

    if (networkPermission?.agents?.view) {
      panels.push(agentsPanel);
    }

    if (networkPermission?.accounts?.view) {
      panels.push(accountsPanel);
    }

    if (
      networkPermission?.suppliers?.view &&
      currentUser?.company?.profile?.initials !== CompanyProfile.MV
    ) {
      panels.push(suppliersPanel);
    }

    return panels;
  };

  const networkPanes = useMemo(() => {
    switch (currentUser?.company?.profile?.initials) {
      case CompanyProfile.MV:
        return handleNetworkTabsByPermission();
      // return [customersPanel, agentsPanel, accountsPanel];
      case CompanyProfile.FM:
        return [customersPanel, suppliersPanel];
      case CompanyProfile.SP:
        return [customersPanel, suppliersPanel];
      case CompanyProfile.ST:
        return [agentsPanel];
      default:
        return [];
    }
  }, [currentUser?.company?.profile?.initials, networkPermission]);

  const [selectedPanel, setSelectedPanel] = useState(0);

  useEffect(() => {
    if (!tab) return;

    const index = networkPanes.findIndex((panel) => panel.key === tab);

    const canChangePanel = index > -1 && selectedPanel !== index;

    if (canChangePanel) setSelectedPanel(index);
  }, [tab, networkPanes, setSelectedPanel]);

  useEffect(() => {
    if (!selectedPanel) return;
    history.replace(`${PRIVATE_ROUTES.NETWORK}?tab=${networkPanes[selectedPanel].key}`);
  }, [selectedPanel]);

  const renderProjectFilter = () => {
    //TODO: move this somewhere else
    return;

    if (!isFMVersion) return;

    return (
      <Grid container justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <ProjectFieldContainer>
            <ProjectLabel>{`${translate("projects")}:`}</ProjectLabel>
            <SkeletonComponent isLoading={loadingProjects}>
              <AutoSuggest
                tagDataCy={`item-project-filter-by`}
                dataCy={`project-filter-by-select`}
                placeholder={translate("select")}
                multiple
                options={currentUser?.projects}
                value={projects}
                onBlur={() => {
                  setIsProjectsFocused(false);
                  if (hasProjectChanged) {
                    handleUpdateUserProjectsView();
                  }
                }}
                onFocus={() => setIsProjectsFocused(true)}
                getOptionSelected={(option, value: any) => {
                  return option?.id === value?.id;
                }}
                getOptionLabel={(option: any) => option?.name || ""}
                onChange={(values, reason: any) => {
                  setHasProjectChanged(true);
                  setProjects(values);
                  if (reason === "remove-option" && !isProjectsFocused) {
                    if (updateProjectTimeOut) clearTimeout(updateProjectTimeOut);

                    setUpdateProjectTimeOut(
                      setTimeout(() => {
                        setIsAsyncUpdate(true);
                      }, 1500)
                    );
                  }
                }}
                limitTags={1}
              />
            </SkeletonComponent>
          </ProjectFieldContainer>
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      {!isMoving(currentUser?.company) ? (
        <SubHeader title={translate("network")}>
          {false && (
            <Button
              startIcon={<InsertLinkIcon htmlColor="#FFFFFF" />}
              customColor={Color.SECONDARY}
              className={classes.buttonLink}
              data-cy="button-link-account"
            >
              {translate("linkAccounts")}
            </Button>
          )}
        </SubHeader>
      ) : null}

      {renderProjectFilter()}

      <div className={classes.mainDiv}>
        <UITabMaterial
          useInitialPadding
          leftPanels={networkPanes}
          selectedLeftPanel={selectedPanel}
          onChangeLeftPanel={(_, panel) => {
            setSelectedPanel(panel);
          }}
        />
      </div>
    </div>
  );
};
